<template>
    <StickyScrollContainer
        :offset="$store.getters['ui/pageScrollTopOffset']"
        :scroll-distance="max"
        emit-more
        @scrolled="onScroll"
    >
        <div
            class="flex flex-col"
            :style="{ maxHeight: `calc(100vh - ${$store.getters['ui/pageScrollTopOffset']}px)` }"
        >
            <slot name="top">
                <Blocks
                    v-if="builderBlock"
                    :path="'top'"
                    :parent="builderBlock.id"
                    :blocks="top"
                />
            </slot>
            <Timeline
                ref="timeline"
                v-resize.immediate="evaluateTimelineFit"
                :direction="direction"
                :events="events"
                :current="progress"
                :max="max"
                :style="timelineStyles"
            />
            <slot name="bottom">
                <Blocks
                    v-if="builderBlock"
                    path="bottom"
                    :parent="builderBlock.id"
                    :blocks="bottom"
                />
            </slot>
        </div>
    </StickyScrollContainer>
</template>

<script lang="ts">
import { type PropType, defineComponent, ref, useTemplateRef } from 'vue';
import type { BuilderBlock } from '@builder.io/sdk-vue';
import { Blocks } from '@builder.io/sdk-vue';
import StickyScrollContainer from '@/components/StickyScrollContainer';
import { useWindowSize } from '@/composables/windowSize';
import { isOverflown } from '@/helpers/dom';
import { useScreenSize } from '@/composables/screenSize';
import Timeline from './Timeline.vue';
import type { TimelineEvent } from './Timeline.types';

/**
 * Timeline component that is sticky on the screen and scrolls & reveals the timeline events as the user scrolls.
 */
export default defineComponent({
    name: 'TimelineStickyScroll',
    components: {
        StickyScrollContainer,
        Timeline,
        Blocks,
    },
    props: {
        /**
         * The events to display in the timeline.
         */
        events: {
            type: Array as PropType<TimelineEvent[]>,
            required: true,
        },
        /** scroll distance in px for timeline */
        max: {
            type: Number,
            required: true,
        },
        /** styles for the timeline container */
        timelineStyles: {
            type: String,
            default: undefined,
        },
        /** top block for builder.io usage */
        top: {
            type: Array as PropType<BuilderBlock[]>,
            default: () => [],
        },
        /** bottom block for builder.io usage */
        bottom: {
            type: Array as PropType<BuilderBlock[]>,
            default: () => [],
        },
        /** builder block for builder.io usage */
        builderBlock: {
            type: Object as PropType<BuilderBlock>,
            default: undefined,
        },
    },
    setup() {
        const { height } = useWindowSize();
        const { minLG } = useScreenSize();

        return {
            minLG,
            windowHeight: height,
            progress: ref(0),
            screenFitsHorizontalDirection: ref(minLG.value),
            timelineRef: useTemplateRef<InstanceType<typeof Timeline>>('timeline'),
        };
    },
    computed: {
        direction() {
            return this.screenFitsHorizontalDirection ? 'horizontal' : 'vertical';
        },
    },
    watch: {
        minLG() {
            this.reevaluateTimelineFit();
        },
        windowHeight() {
            this.reevaluateTimelineFit();
        },
    },
    mounted() {
        this.evaluateTimelineFit();
    },
    methods: {
        onScroll(distance: number) {
            // adding a bit of window height will induce the progress a bit earlier while already being on screen
            this.progress = Math.max(0, distance + this.windowHeight / 4);
        },
        evaluateTimelineFit() {
            if (!this.timelineRef || !this.screenFitsHorizontalDirection) return;

            this.screenFitsHorizontalDirection = !isOverflown(this.timelineRef.$el);
            this.direction = this.screenFitsHorizontalDirection ? 'horizontal' : 'vertical';
        },
        async reevaluateTimelineFit() {
            this.screenFitsHorizontalDirection = this.minLG;
            await this.$nextTick();
            this.evaluateTimelineFit();
        },
    },
});
</script>
